import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { adminGet, adminPost } from "@/lib/admin-api";
import { WISHLIST_ITEMS } from "@shared/wishlist";

interface ItemDraft {
  description: string;
  quantity: number;
  unitPrice: number;
  wishItemId?: string;
}

interface DonorDraft {
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  phone?: string;
  line1?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

interface ExistingInvoice {
  id: string;
  invoiceNumber: string;
  donorName: string;
  donorEmail: string;
  items: Array<{ description: string; quantity: number; unitPrice: number; wishItemId?: string }>;
  notes: string;
  issueDate: string;
  dueDate: string;
  status: string;
  total: number;
  primaryWishItemId?: string;
  paymentUrl: string;
}

function InvoiceEditorInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [donor, setDonor] = React.useState<DonorDraft>({
    email: "",
    firstName: "",
    lastName: "",
    country: "US",
  });
  const [items, setItems] = React.useState<ItemDraft[]>([
    { description: "", quantity: 1, unitPrice: 0 },
  ]);
  const [notes, setNotes] = React.useState<string>("");
  const [dueDate, setDueDate] = React.useState<string>(
    new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  );
  const [primaryWishItemId, setPrimaryWishItemId] = React.useState<string>("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [existing, setExisting] = React.useState<ExistingInvoice | null>(null);

  React.useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await adminGet<{ items: ExistingInvoice[] }>(`/api/admin/invoices/list`);
        const found = res.items.find((i) => i.id === id);
        if (found) {
          setExisting(found);
          const [first, last] = (found.donorName || "").split(/\s+/, 2);
          setDonor({
            email: found.donorEmail,
            firstName: first ?? "",
            lastName: last ?? "",
            country: "US",
          });
          setItems(
            found.items.map((it) => ({
              description: it.description,
              quantity: it.quantity,
              unitPrice: it.unitPrice,
              wishItemId: it.wishItemId,
            })),
          );
          setNotes(found.notes || "");
          setDueDate(found.dueDate);
          setPrimaryWishItemId(found.primaryWishItemId ?? "");
        }
      } catch (err) {
        setError(String(err));
      }
    })();
  }, [id, isEdit]);

  function patchItem(idx: number, patch: Partial<ItemDraft>) {
    setItems((items) => items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItems((items) => [...items, { description: "", quantity: 1, unitPrice: 0 }]);
  }
  function removeItem(idx: number) {
    setItems((items) => items.filter((_, i) => i !== idx));
  }

  function quickAddWishItem(wishId: string) {
    const wi = WISHLIST_ITEMS.find((w) => w.id === wishId);
    if (!wi) return;
    setItems((items) => [
      ...items,
      {
        description: wi.name,
        quantity: 1,
        unitPrice: wi.cost_usd,
        wishItemId: wi.id,
      },
    ]);
    setPrimaryWishItemId(wishId);
  }

  const subtotal = items.reduce((s, it) => s + it.unitPrice * it.quantity, 0);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (!donor.email || !donor.firstName) {
      setError("Donor email + first name are required.");
      return;
    }
    if (items.length === 0 || items.some((it) => !it.description || !it.unitPrice)) {
      setError("All items need a description and a non-zero unit price.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit && existing) {
        // For now, edit only updates the Firestore mirror; deeper WC update is a follow-up.
        await adminPost("/api/admin/invoices/update", {
          id: existing.id,
          updates: {
            notes,
            dueDate,
            primaryWishItemId,
          },
        });
        navigate("/admin/invoices");
      } else {
        const created = await adminPost<{ id: string }>("/api/admin/invoices/create", {
          donor: {
            email: donor.email,
            firstName: donor.firstName,
            lastName: donor.lastName,
            company: donor.company,
            phone: donor.phone,
            address: donor.line1
              ? {
                  line1: donor.line1,
                  city: donor.city ?? "",
                  state: donor.state ?? "",
                  postcode: donor.postcode ?? "",
                  country: donor.country ?? "US",
                }
              : undefined,
          },
          items,
          notes,
          dueDate,
          primaryWishItemId: primaryWishItemId || undefined,
        });
        navigate(`/admin/invoices/${created.id}`);
      }
    } catch (err) {
      setError(String(err));
      setSaving(false);
    }
  }

  return (
    <PageShell title={isEdit ? "Invoice · Edit" : "Invoice · New"} status="admin" width="wide">
      <Seo title="Invoice editor" description="" path="/admin/invoices" noindex />
      <AdminTopNav />
      <div className="mb-6">
        <Link
          to="/admin/invoices"
          className="font-mono text-[10px] uppercase tracking-widest text-ink-3 hover:text-ink"
        >
          ← All invoices
        </Link>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-6">
          {error}
        </div>
      )}

      <form onSubmit={onSave} className="space-y-10 max-w-3xl">
        <section>
          <h2 className="font-serif text-xl text-ink mb-4">Donor</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email" required value={donor.email} onChange={(v) => setDonor({ ...donor, email: v })} type="email" />
            <Field label="First name" required value={donor.firstName} onChange={(v) => setDonor({ ...donor, firstName: v })} />
            <Field label="Last name" value={donor.lastName} onChange={(v) => setDonor({ ...donor, lastName: v })} />
            <Field label="Company (opt.)" value={donor.company ?? ""} onChange={(v) => setDonor({ ...donor, company: v })} />
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl text-ink">Line items</h2>
            <div className="flex gap-2">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    quickAddWishItem(e.target.value);
                    e.target.value = "";
                  }
                }}
                className="border border-hairline bg-paper px-3 py-2 font-mono text-xs"
                defaultValue=""
              >
                <option value="">+ Add wish-list item</option>
                {WISHLIST_ITEMS.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} — US${w.cost_usd}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={addItem}
                className="border border-ink text-ink px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-paper-2"
              >
                + Empty row
              </button>
            </div>
          </div>

          <div className="border border-hairline divide-y divide-hairline">
            {items.map((it, idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 gap-3 px-4 py-3 items-center"
              >
                <div className="col-span-6">
                  <input
                    placeholder="Description"
                    value={it.description}
                    onChange={(e) => patchItem(idx, { description: e.target.value })}
                    className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
                  />
                  {it.wishItemId && (
                    <span className="font-mono text-[10px] text-claude uppercase tracking-widest mt-1 block">
                      Wish: {it.wishItemId}
                    </span>
                  )}
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    min={1}
                    placeholder="Qty"
                    value={it.quantity}
                    onChange={(e) => patchItem(idx, { quantity: Number(e.target.value) })}
                    className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Unit price"
                    value={it.unitPrice}
                    onChange={(e) => patchItem(idx, { unitPrice: Number(e.target.value) })}
                    className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
                  />
                </div>
                <div className="col-span-1 font-mono text-xs text-right text-ink">
                  ${(it.quantity * it.unitPrice).toFixed(2)}
                </div>
                <div className="col-span-1 text-right">
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="font-mono text-[10px] uppercase tracking-widest text-state-pending hover:text-ink"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
            <div className="px-4 py-3 flex justify-between font-mono text-sm bg-paper-2">
              <span className="text-ink-3 uppercase tracking-widest text-[10px]">Subtotal</span>
              <span className="text-ink">US${subtotal.toFixed(2)}</span>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Due date" type="date" value={dueDate} onChange={setDueDate} />
          <div>
            <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1" htmlFor="notes">
              Notes
            </label>
            <textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
            />
          </div>
        </section>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-ink text-paper px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-ink-2 disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create invoice"}
          </button>
          {existing?.paymentUrl && (
            <a
              href={existing.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs uppercase tracking-widest text-ink underline decoration-hairline hover:decoration-ink"
            >
              Open payment link →
            </a>
          )}
        </div>
      </form>
    </PageShell>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
}) {
  return (
    <div>
      <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1">
        {label}
        {required && <span className="text-state-pending"> *</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm focus:outline-none focus:border-ink"
      />
    </div>
  );
}

export function InvoiceEditor() {
  return (
    <AdminGuard allow={["admin"]}>
      <InvoiceEditorInner />
    </AdminGuard>
  );
}
